import { hasPriceDifference, toCents, toDollars } from '@/utils/utilities.mjs';
import * as ShopifyMultipleDiscountCodesStrategy from './strategies/ShopifyMultipleDiscountCodesStrategy';
import * as ShopifySingleDiscountCodeStrategy from './strategies/ShopifySingleDiscountCodeStrategy';

const MAX_CACHE_SIZE = 100;

/**
 * Memo by first-arg object identity (WeakMap) + LRU for primitives; stale if inputs mutate in place.
 */
const remember = (fn) => {
  // Object first-arg: keyed by identity; entries are released with the object.
  const weakCache = new WeakMap();

  // Primitive first-arg: size-capped LRU map.
  const primitiveCache = new Map();

  return (...args) => {
    const [firstArg, ...restArgs] = args;

    if (firstArg !== null && typeof firstArg === 'object') {
      if (!weakCache.has(firstArg)) {
        weakCache.set(firstArg, new Map());
      }
      const inner = weakCache.get(firstArg);
      const key = JSON.stringify(restArgs);

      if (!inner.has(key)) {
        inner.set(key, fn(...args));
      }

      return inner.get(key);
    }

    const key = JSON.stringify(args);

    if (primitiveCache.has(key)) {
      // Move to end to maintain LRU order (Map preserves insertion order).
      const value = primitiveCache.get(key);
      primitiveCache.delete(key);
      primitiveCache.set(key, value);

      return value;
    }

    if (primitiveCache.size >= MAX_CACHE_SIZE) {
      primitiveCache.delete(primitiveCache.keys().next().value);
    }

    const value = fn(...args);
    primitiveCache.set(key, value);

    return value;
  };
};

export const variantPricing = remember(
  (variant, discountSettings) => {
    const fullPrice = variantFullPrice(variant, discountSettings);
    const salePrice =
      variantSalePrice(variant, discountSettings) ?? fullPrice;

    return {
      full: fullPrice,
      sale: salePrice,
      discount: toDollars(toCents(fullPrice) - toCents(salePrice)),
      hasPriceDifference: hasPriceDifference(fullPrice, salePrice),
    };
  },
);

export const variantSalePrice = remember((variant, discountSettings) => {
  const strategy = getCurrentStrategy(discountSettings);
  if (!strategy) return null;

  return strategy.variantSalePrice(variant, discountSettings);
});

export const variantFullPrice = remember((variant, discountSettings) => {
  const strategy = getCurrentStrategy(discountSettings);
  if (!strategy) return null;

  return strategy.variantFullPrice(variant, discountSettings);
});

export const promotionalCopy = remember(
  (discountSettings, variation, variant = null) => {
    const strategy = getCurrentStrategy(discountSettings);
    if (!strategy) return null;

    return strategy.promotionalCopy(discountSettings, variation, variant);
  },
);

// One dispatcher for the gallery promo overlay: resolves the group/settings once
// per strategy and returns { code, message } together, e.g. { code: 'LUXE25', message: '25% Off' }.
export const overlayPromo = remember(
  (discountSettings, variant = null) => {
    const strategy = getCurrentStrategy(discountSettings);
    if (!strategy) return { code: null, message: null };

    return strategy.overlayPromo(discountSettings, variant);
  },
);

export const cartLinePricing = remember(
  (cartLine, discountSettings) => {
    const fullPrice = cartLineFullPrice(cartLine, discountSettings);
    const salePrice =
      cartLineSalePrice(cartLine, discountSettings) ?? fullPrice;

    return {
      full: fullPrice,
      sale: salePrice,
      discount: toDollars(toCents(fullPrice) - toCents(salePrice)),
      hasPriceDifference: hasPriceDifference(fullPrice, salePrice),
    };
  },
);

export const cartLineSalePrice = remember((variant, discountSettings) => {
  const strategy = getCurrentStrategy(discountSettings);
  if (!strategy) return null;

  return strategy.cartLineSalePrice(variant, discountSettings);
});

export const cartLineFullPrice = remember((variant, discountSettings) => {
  const strategy = getCurrentStrategy(discountSettings);
  if (!strategy) return null;

  return strategy.cartLineFullPrice(variant, discountSettings);
});

export const cartTotal = remember((cart, discountSettings) => {
  const strategy = getCurrentStrategy(discountSettings);
  if (!strategy) return null;

  return strategy.cartTotal(cart, discountSettings);
});

export const cartSubtotal = remember((cart, discountSettings) => {
  const strategy = getCurrentStrategy(discountSettings);
  if (!strategy) return null;

  return strategy.cartSubtotal(cart, discountSettings);
});

export const cartSavings = remember((cart, discountSettings) => {
  const strategy = getCurrentStrategy(discountSettings);
  if (!strategy) return null;

  return strategy.cartSavings(cart, discountSettings);
});

const getCurrentStrategy = (discountSettings) => {
  switch (discountSettings?.strategy) {
    case 'multiple':
      return ShopifyMultipleDiscountCodesStrategy;
    case 'single':
      return ShopifySingleDiscountCodeStrategy;
    default:
      return null;
  }
};
